/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and
 * international treaties. Unauthorized use, reproduction, distribution, or
 * modification of any content from Knowledge for Decisions (K4D) is strictly
 * prohibited. For inquiries regarding the use or licensing of K4D's
 * copyrighted materials, please contact us at contact@k4d.com.br. "Knowledge
 * for Decisions (K4D)" and the slogan "Using data to make precise decisions"
 * are registered trademarks of Knowledge for Decisions (K4D).
 */

import { useCallback, useEffect, useRef, useState } from 'react';

/**
 * Rows the preview table draws at first, and adds per step.
 *
 * The Univen property export is 1.556 rows by 80 columns: drawn whole, that
 * is 124 mil cells, redrawn on every answer in the names panel, and Chrome
 * killed the tab (SIGILL, out of memory) after a few answers. A hundred rows
 * fill several screens and keep each redraw small.
 */
export const ROW_WINDOW_STEP = 100;

/**
 * How many rows to draw after one more step.
 *
 * @param shown - Rows drawn now.
 * @param total - Rows in the preview.
 * @returns The next count, never past the total.
 */
export function nextRowWindow(shown: number, total: number): number {
  return Math.min(shown + ROW_WINDOW_STEP, total);
}

/**
 * A growing window over the preview rows.
 *
 * Starts at {@link ROW_WINDOW_STEP} rows and grows by the same step when the
 * sentinel at the bottom of the table scrolls into view, or when the person
 * asks for more. A new set of rows (another filter, another session) starts
 * over at the first step.
 *
 * @param total - Rows in the preview.
 * @param resetKey - Changes whenever the row set is a different one.
 * @returns The count to draw, a "show more" handler and the sentinel ref.
 */
export function useRowWindow(total: number, resetKey: string) {
  const [shown, setShown] = useState(ROW_WINDOW_STEP);
  const sentinelRef = useRef<HTMLTableRowElement | null>(null);

  useEffect(() => { setShown(ROW_WINDOW_STEP); }, [resetKey]);

  const showMore = useCallback(() => setShown((now) => nextRowWindow(now, total)), [total]);

  useEffect(() => {
    const sentinel = sentinelRef.current;
    if (!sentinel || typeof IntersectionObserver === 'undefined') return undefined;
    const observer = new IntersectionObserver((entries) => {
      if (entries.some((entry) => entry.isIntersecting)) showMore();
    });
    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [showMore, shown]);

  return { shown: Math.min(shown, total), showMore, sentinelRef };
}
